import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import Logo from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";
import SiteFooter from "@/components/SiteFooter";
import PasswordStrength, { isPasswordAcceptable } from "@/components/PasswordStrength";
import TurnstileWidget from "@/components/TurnstileWidget";
import { toast } from "sonner";
import { Mail, RotateCw, ShieldAlert, Clock, Send } from "lucide-react";
import axios from "axios";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PW_MIN = 8;

export default function Auth() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "signup" ? "signup" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [pwScore, setPwScore] = useState(null);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pendingVerificationEmail, setPendingVerificationEmail] = useState(null);
  const [moderationBlock, setModerationBlock] = useState(null); // { status, reason, suspended_until, appeal_pending }
  const { user, login, signup, resendVerification } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      navigate(user.onboarded ? "/dashboard" : "/onboarding", { replace: true });
    }
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (mode === "signup") {
      if (password.length < PW_MIN) { toast.error(`Password must be at least ${PW_MIN} characters.`); return; }
      if (!isPasswordAcceptable(password, pwScore)) { toast.error("Password is too easy to guess. Add unusual words, mix in numbers/symbols, or make it longer."); return; }
      if (process.env.REACT_APP_TURNSTILE_SITE_KEY && !turnstileToken) {
        toast.error("Please complete the human-check below before signing up.");
        return;
      }
    }
    setBusy(true);
    try {
      if (mode === "signup") {
        const res = await signup(email, password, name, turnstileToken);
        if (res?.verification_required) {
          setPendingVerificationEmail(res.email || email);
          toast.success("Account created. Verify your email to continue.");
        }
      } else {
        const u = await login(email, password);
        toast.success("Welcome back.");
        navigate(u.onboarded ? "/dashboard" : "/onboarding");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Banned/suspended → show the appeal screen instead of a toast
      if (err?.response?.status === 403 && typeof detail === "object" && detail?.status && (detail.status === "banned" || detail.status === "suspended")) {
        setModerationBlock({ ...detail, email });
        return;
      }
      if (err?.response?.status === 403 && detail === "EMAIL_NOT_VERIFIED") {
        setPendingVerificationEmail(email);
        toast.error("Verify your email before logging in.");
      } else if (typeof detail === "string") {
        toast.error(detail);
      } else {
        toast.error("Authentication failed");
      }
    } finally {
      setBusy(false);
    }
  };

  const onResend = async () => {
    if (!pendingVerificationEmail) return;
    setBusy(true);
    try {
      await resendVerification(pendingVerificationEmail);
      toast.success("Verification email sent. Check your inbox.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not resend. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  // Banned / suspended screen with appeal form
  if (moderationBlock) {
    return (
      <ModerationBlockScreen
        block={moderationBlock}
        password={password}
        onBack={() => { setModerationBlock(null); setPassword(""); }}
      />
    );
  }

  // Check-your-email screen
  if (pendingVerificationEmail) {
    return (
      <div className="min-h-screen flex flex-col">
        <header className="border-b border-foreground/30 glass-strong">
          <div className="max-w-[1600px] mx-auto px-5 md:px-12 h-14 md:h-16 flex items-center justify-between">
            <Link to="/"><Logo /></Link>
            <ThemeToggle />
          </div>
        </header>
        <div className="flex-1 flex items-center justify-center px-5 sm:px-6 py-10 sm:py-12">
          <div className="w-full max-w-lg panel-glass p-6 sm:p-8 md:p-12 relative corner-marks" data-testid="auth-verify-pending">
            <div className="terminal-label mb-3 text-primary">[ VERIFY.EMAIL ]</div>
            <Mail className="w-10 h-10 text-foreground/70 mb-6" strokeWidth={1.25} />
            <h1 className="serif-mod text-3xl sm:text-4xl tracking-tight mb-3" style={{ fontWeight: 500 }}>
              Check your inbox.
            </h1>
            <p className="text-sm text-foreground/75 leading-relaxed mb-8">
              We sent a verification link to <span className="text-primary font-mono">{pendingVerificationEmail}</span>. Click the link to activate your account and start your free trial.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                data-testid="auth-resend-verification"
                onClick={onResend}
                disabled={busy}
                className="btn-secondary inline-flex items-center gap-2"
              >
                <RotateCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} /> Resend email
              </button>
              <button
                data-testid="auth-back-to-login"
                onClick={() => { setPendingVerificationEmail(null); setMode("login"); }}
                className="btn-ghost"
              >
                Back to log in
              </button>
            </div>
            <p className="mt-8 text-[10px] tracking-[0.2em] uppercase text-foreground/45 font-mono">
              Tip: link valid for 48 hours · Check spam if missing
            </p>
          </div>
        </div>
        <SiteFooter variant="site" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-foreground/30 glass-strong">
        <div className="max-w-[1600px] mx-auto px-5 md:px-12 h-14 md:h-16 flex items-center justify-between gap-2">
          <Link to="/"><Logo /></Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <ThemeToggle />
            <button
              data-testid="auth-toggle-mode"
              onClick={() => setMode(mode === "login" ? "signup" : "login")}
              className="btn-ghost !px-2 sm:!px-3 text-[10px] sm:text-xs"
            >
              <span className="hidden sm:inline">{mode === "login" ? "Need an account? Sign up" : "Have an account? Log in"}</span>
              <span className="sm:hidden">{mode === "login" ? "Sign up" : "Log in"}</span>
            </button>
          </div>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-5 sm:px-6 py-10 sm:py-12">
        <div className="w-full max-w-md panel-glass p-6 sm:p-8 md:p-10 relative corner-marks">
          <div className="terminal-label mb-2 text-primary">
            [ {mode === "signup" ? "ACCOUNT.NEW" : "ACCOUNT.AUTH"} ]
          </div>
          <h1 className="font-display text-3xl uppercase font-bold mb-2 tracking-tight">
            {mode === "signup" ? "Start your trial" : "Log in"}
          </h1>
          <p className="text-sm text-muted-foreground mb-8">
            {mode === "signup"
              ? "3 free tailored applications. No card required."
              : "Welcome back to Sisyphean."}
          </p>

          {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR
              REDIRECT URLS, THIS BREAKS THE AUTH. */}
          <button
            type="button"
            data-testid="auth-google-btn"
            onClick={() => {
              const redirectUrl = window.location.origin + "/dashboard";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="w-full mb-5 h-11 inline-flex items-center justify-center gap-3 border border-foreground/30 hover:border-foreground/60 transition-colors bg-surface text-foreground text-sm font-mono uppercase tracking-[0.18em]"
          >
            <svg width="16" height="16" viewBox="0 0 18 18" aria-hidden="true">
              <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.79 2.72v2.26h2.9c1.69-1.56 2.67-3.86 2.67-6.62z"/>
              <path fill="#34A853" d="M9 18c2.43 0 4.47-.81 5.96-2.18l-2.9-2.26c-.81.54-1.84.86-3.06.86-2.35 0-4.34-1.59-5.05-3.71H.96v2.33A9 9 0 0 0 9 18z"/>
              <path fill="#FBBC05" d="M3.95 10.71a5.41 5.41 0 0 1 0-3.42V4.96H.96a9 9 0 0 0 0 8.08l2.99-2.33z"/>
              <path fill="#EA4335" d="M9 3.58c1.32 0 2.51.46 3.44 1.35l2.58-2.58A9 9 0 0 0 .96 4.96l2.99 2.33C4.66 5.17 6.65 3.58 9 3.58z"/>
            </svg>
            Continue with Google
          </button>

          <div className="flex items-center gap-3 mb-5 text-[10px] tracking-[0.25em] uppercase text-muted-foreground/60 font-mono">
            <span className="flex-1 h-px bg-foreground/15" />
            or use email
            <span className="flex-1 h-px bg-foreground/15" />
          </div>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="auth-form">
            {mode === "signup" && (
              <div>
                <label className="terminal-label">Name</label>
                <input
                  data-testid="auth-name"
                  className="input-base mt-2"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Doe"
                />
              </div>
            )}
            <div>
              <label className="terminal-label">Email</label>
              <input
                data-testid="auth-email"
                type="email"
                required
                className="input-base mt-2"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@studio.com"
              />
            </div>
            <div>
              <label className="terminal-label">Password</label>
              <input
                data-testid="auth-password"
                type="password"
                required
                className="input-base mt-2"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
              />
              {mode === "signup" && (
                <PasswordStrength
                  password={password}
                  userInputs={[email, name].filter(Boolean)}
                  onScoreChange={setPwScore}
                />
              )}
            </div>
            {mode === "signup" && <TurnstileWidget onToken={setTurnstileToken} />}
            <button
              data-testid="auth-submit"
              type="submit"
              disabled={busy || (mode === "signup" && !isPasswordAcceptable(password, pwScore))}
              className="btn-primary w-full mt-6"
            >
              {busy ? "..." : mode === "signup" ? "Create account →" : "Log in →"}
            </button>
            {mode === "login" && (
              <div className="text-right">
                <Link to="/forgot-password" data-testid="auth-forgot-link" className="text-xs text-foreground/60 hover:text-primary underline-offset-4 hover:underline">
                  Forgot password?
                </Link>
              </div>
            )}
          </form>
        </div>
      </div>
      <SiteFooter variant="site" />
    </div>
  );
}


function ModerationBlockScreen({ block, password, onBack }) {
  const [appeal, setAppeal] = useState("");
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const isBan = block.status === "banned";
  const until = block.suspended_until ? new Date(block.suspended_until) : null;
  const hoursLeft = until ? Math.max(0, Math.round((until.getTime() - Date.now()) / 3600000)) : null;

  const submit = async (e) => {
    e.preventDefault();
    if (appeal.trim().length < 20) {
      toast.error("Tell us why we should reconsider. At least a sentence or two.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/auth/appeal`, { email: block.email, password, message: appeal.trim() });
      toast.success("Appeal sent. We'll review it shortly.");
      setSubmitted(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not submit appeal.";
      toast.error(typeof msg === "string" ? msg : "Could not submit appeal.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-foreground/30 glass-strong">
        <div className="max-w-[1600px] mx-auto px-5 md:px-12 h-14 md:h-16 flex items-center justify-between">
          <Link to="/"><Logo /></Link>
          <ThemeToggle />
        </div>
      </header>
      <div className="flex-1 flex items-center justify-center px-5 sm:px-6 py-10 sm:py-12">
        <div className="w-full max-w-xl panel-glass p-6 sm:p-8 md:p-10 corner-marks" data-testid="auth-moderation-block">
          <div className="flex items-center gap-3 mb-5">
            <ShieldAlert className={isBan ? "w-7 h-7 text-primary" : "w-7 h-7 text-amber-500"} strokeWidth={1.5} />
            <div className="terminal-label text-primary">[ {isBan ? "ACCOUNT BANNED" : "ACCOUNT SUSPENDED"} ]</div>
          </div>
          <h1 className="serif-mod text-3xl sm:text-4xl tracking-tight mb-3" style={{ fontWeight: 500 }}>
            {isBan ? "Your account has been banned." : "Your account is temporarily suspended."}
          </h1>
          <div className="brut-tile p-4 mb-5" data-testid="moderation-reason-block">
            <div className="terminal-label mb-1">Reason</div>
            <p className="text-sm text-foreground/85">{block.reason}</p>
            {!isBan && until && (
              <div className="mt-3 flex items-center gap-2 text-xs text-foreground/65 font-mono">
                <Clock className="w-3 h-3" />
                {hoursLeft > 0 ? <>Suspension ends in {hoursLeft} hour{hoursLeft === 1 ? "" : "s"} ({until.toLocaleString()})</> : <>Suspension expires shortly. Try logging in again in a few minutes.</>}
              </div>
            )}
          </div>

          {submitted || block.appeal_pending ? (
            <div className="brut-tile p-5 text-center" style={{ borderColor: "hsl(var(--accent-green))" }} data-testid="moderation-appeal-sent">
              <div className="terminal-label mb-2" style={{ color: "hsl(var(--accent-green))" }}>[ APPEAL UNDER REVIEW ]</div>
              <p className="text-sm text-foreground/80">
                {submitted
                  ? "Your appeal has been sent to hello@sisyphean.ai. We'll review it within a few days and let you know."
                  : `You already have an appeal under review${block.appeal_submitted_at ? ` (submitted ${new Date(block.appeal_submitted_at).toLocaleDateString()})` : ""}. Hang tight.`}
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="moderation-appeal-form" noValidate>
              <div>
                <div className="flex items-baseline justify-between mb-2">
                  <span className="terminal-label">Appeal</span>
                  <span className="text-[10px] tracking-[0.18em] uppercase text-primary font-mono">Required</span>
                </div>
                <textarea
                  value={appeal}
                  onChange={(e) => setAppeal(e.target.value)}
                  rows={6}
                  maxLength={5000}
                  placeholder="Explain why you believe this decision should be reconsidered. The more specific you are, the better."
                  className="input-base resize-y leading-relaxed"
                  data-testid="moderation-appeal-input"
                />
                <div className="flex justify-between items-center mt-1 text-[10px] tracking-[0.2em] uppercase font-mono">
                  <span className={appeal.trim().length < 20 ? "text-amber-500" : "text-foreground/45"}>
                    {appeal.trim().length < 20 ? `${20 - appeal.trim().length} more characters needed` : "Looks good"}
                  </span>
                  <span className="text-foreground/45">{appeal.length} / 5000</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-1">
                <button
                  type="submit"
                  disabled={sending}
                  className="btn-primary inline-flex items-center justify-center gap-3 disabled:opacity-60"
                  data-testid="moderation-appeal-submit"
                >
                  {sending ? "Sending..." : <>Send appeal <Send className="w-3 h-3" /></>}
                </button>
                <button
                  type="button"
                  onClick={onBack}
                  className="btn-secondary"
                  data-testid="moderation-appeal-back"
                >
                  Back to login
                </button>
              </div>
              <p className="text-[11px] text-foreground/45 mt-3 font-mono">
                Your appeal goes to <span className="text-foreground/65">hello@sisyphean.ai</span>. Allow a few days for review.
              </p>
            </form>
          )}
        </div>
      </div>
      <SiteFooter variant="site" />
    </div>
  );
}

